import React, { useEffect, useState } from 'react';
import {
	Avatar,
	Box,
	Button,
	ButtonBase,
	Card,
	CardContent,
	Checkbox,
	Chip,
	Dialog,
	DialogContent,
	Grid,
	IconButton,
	Paper,
	Radio,
	Skeleton,
	Stack,
	Step,
	StepLabel,
	Stepper,
	TextField,
	Typography,
} from '@mui/material';

import GroupsIcon from '@mui/icons-material/Groups';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AssignmentIcon from '@mui/icons-material/Assignment';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ListAltIcon from '@mui/icons-material/ListAlt';
import PersonIcon from '@mui/icons-material/Person';
import AddCommentIcon from '@mui/icons-material/AddComment';
import MainGrid from '../../../components/MainGrid';
import { useMainCustomStyle } from '../../../assets/theme/Theme';
import { useLocation, useNavigate } from 'react-router-dom';
import {
	getAccessToken,
	getElderHomeId,
	getUserId,
	getUserName,
	getUserType,
} from '../../../Utils/AuthUtil';
import {
	getInitialCarePlan,
	getInitialPlanElderId,
} from '../../../context/MainProvider';
import { USER_TYPES } from '../../../Utils/Enums';
import {
	cancelApiRequest,
	generateInitialCareGoals,
	generateInitialCarePlan,
	getPlanTemplate,
	uploadInitialCarePlan,
} from '../../../services/CarePlanServices';
import {
	addInitialCarePlan,
	fetchElder,
	fetchExternalElder,
	getResidentIds,
	storeUpdatedElder,
} from '../../../services/ElderService';
import { sanitizeTitle } from '../../../Utils/Helpers';
import { PlanDocument } from '../../../components/PDFDocument';
import { pdf } from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import { CircleClose, PlanIcon } from '../../../assets/icons/Icons';
import ToastBanner from '../../../components/ToastBanner';
import GeneratedInitialPlan from './GeneratedInitialPlan';
import { Close, Edit, Newspaper } from '@mui/icons-material';
import Dropzone from 'react-dropzone';
import { border, dark, surface, teal } from '../../../assets/theme/Colors';
import {
	getTemplateByScope,
	getTemplateByType,
} from '../../../services/UserService';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// eslint-disable-next-line no-undef
const central_db_host = process.env.REACT_APP_UMS_API_URL;

const INITIAL_ERROR_STATE = {
	status: false,
	msg: '',
};

const colors = [
	'#2563eb', // blue
	'#dc2626', // red
	'#00a89d', // green
	'#ca8a04', // yellow
	'#7c3aed', // purple
	'#ea580c', // orange
	'#0891b2', // cyan
	'#db2777', // pink
];

const steps = [
	'Resident profile',
	'Add context',
	'Select categories',
	'Generate & review',
	'Save & download',
];

function PlanTemplatesPopup({
	isModalOpen,
	templates,
	selected,
	onSelect,
	onSubmit,
	onClose,
}) {
	const Template = ({ title, isSelected, onSelectItem }) => {
		return (
			<Box
				onClick={onSelectItem}
				sx={{
					border: isSelected
						? '1px solid #b3ede8'
						: '1px solid #d8e8e8',
					background: isSelected ? '#eefcf9' : '#fff',
					borderRadius: '12px',
					p: 1.5,
					display: 'flex',
					alignItems: 'center',
					gap: 1.5,
					cursor: 'pointer',
					transition: '0.2s',
					'&:hover': {
						borderColor: teal,
						background: '#eefcf9',
					},
				}}>
				<Box
					sx={{
						width: 36,
						height: 36,
						borderRadius: '10px',
						background: isSelected
							? 'rgba(0,196,180,0.15)'
							: surface,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						color: isSelected ? teal : '#6b8888',
					}}>
					<Newspaper />
				</Box>

				<Box sx={{ flex: 1 }}>
					<Typography
						sx={{
							fontSize: 12,
							color: '#6b8888',
							fontWeight: 500,
						}}>
						{title}
					</Typography>
				</Box>

				{isSelected && (
					<CheckCircleIcon
						sx={{
							fontSize: 18,
							color: teal,
						}}
					/>
				)}
			</Box>
		);
	};

	return (
		<Dialog
			open={isModalOpen}
			fullWidth
			maxWidth="lg"
			onClose={onClose}
			scroll="body">
			<DialogContent style={{ padding: '0px 0px 20px 0px' }}>
				<Grid
					container
					sx={{ boxShadow: 3 }}
					padding={'10px'}
					flexDirection={'row'}
					justifyContent={'space-between'}
					alignItems={'center'}>
					<Typography fontWeight={'600'}>
						{'Select care plan template'}
					</Typography>
					<IconButton style={{ padding: '0' }} onClick={onClose}>
						<Close />
					</IconButton>
				</Grid>
				<Grid container padding={'10px'}>
					<Grid container columnSpacing={3} rowSpacing={2}>
						{templates.map((plan, index) => {
							return (
								<Grid key={index} item xs={12} md={12}>
									<Template
										title={plan.templateName}
										isSelected={selected === plan.id}
										onSelectItem={() => onSelect(plan.id)}
									/>
								</Grid>
							);
						})}
					</Grid>
					<Grid
						container
						justifyContent={'flex-end'}
						alignItems={'center'}
						marginTop={'10px'}>
						<Button
							variant="contained"
							disabled={selected === ''}
							style={{
								width: '10%',
								backgroundColor:
									selected === '' ? '#FFFFFF' : teal,
								paddingLeft: '15px',
								paddingRight: '15px',
								fontWeight: '600',
								textTransform: 'none',
								border: `1px solid ${selected === '' ? '#D9D9D9' : teal} `,
							}}
							onClick={onSubmit}>
							Next
						</Button>
					</Grid>
				</Grid>
			</DialogContent>
		</Dialog>
	);
}

export default function InitialCarePlanMain() {
	const { state } = useLocation();
	const token = getAccessToken();
	const homeId = getElderHomeId();
	const { initialCarePlan, storeInitialCarePlan } = getInitialCarePlan();
	const { initialPlanElderId, storeInitialPlanElderId } =
		getInitialPlanElderId();
	const [id, setId] = useState(null);
	const [txt, setTxt] = useState([]);
	const [fileData, setFileData] = useState([]);
	const [idError, setIdError] = useState(INITIAL_ERROR_STATE);
	const [txtError, setTxtError] = useState(INITIAL_ERROR_STATE);
	const [isLoading, setIsLoading] = useState(false);
	const [isPlanGenerated, setIsPlanGenerated] = useState(false);
	const [expandedAccordion, setExpandedAccordion] = useState(null);
	const [isPlanOpen, setIsPlanOpen] = useState(false);
	const [isMultiFileModalOpen, setIsMultiFileModalOpen] = useState(false);
	const [popupTitle, setPopupTitle] = useState('');
	const [selectionData, setSelectionData] = useState({});
	const [selectedData, setSelectedData] = useState([]);
	const [selectedDataError, setSelectedDataError] =
		useState(INITIAL_ERROR_STATE);
	const [popupBtnState, setPopupBtnState] = useState('');
	const [generatedPlan, setGeneratedPlan] = useState({});
	const [sessionId, setSessionId] = useState('');
	const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
	const [loadingMsg, setLoadingMsg] = useState('');
	const [medicalConditionData, setMedicalConditionData] = useState([]);
	const [selectedMedicalCondition, setSelectedMedicalCondition] = useState(
		[],
	);
	const [isMedicalConditionStepComplete, setIsMedicalConditionStepComplete] =
		useState(false);
	const [openLimitationModal, setOpenLimitationModal] = useState(false);
	const [remainingGeneration, setRemainingGeneration] = useState({
		count: 0,
		progress: 0,
		text: '',
	});
	const isDemoUser = getUserType() === USER_TYPES[2].key;
	const [residentIds, setResidentIds] = useState([]);
	const [residentFetchedIds, setResidentFetchedIds] = useState([]);
	const [onShowFileUpload, setOnShowFileUpload] = useState(false);
	const [isGenerationComplete, setIsGenerationComplete] = useState(false);
	const [editing, setEditing] = useState(false);
	const [resident, setResident] = useState({});
	const [residentOriginalData, setResidentOriginalData] = useState({});
	const [editedResident, setEditedResident] = useState({});
	const [editedResidentOriginalData, setEditedResidentOriginalData] =
		useState({});
	const [instruction, setInstruction] = useState('');
	const [showNoResidentBanner, setShowNoResidentBanner] = useState(false);
	const [showTemplates, setShowTemplates] = useState(false);
	const [planTemplates, setPlanTemplates] = useState([]);
	const [selectedTemplate, setSelectedTemplate] = useState('');
	const [isUserHasTemplates, setIsUserHasTemplates] = useState(false);
	const [isProfileEdited, setIsProfileEdited] = useState(false);
	const [noResidentMsg, setNoResidentMsg] = useState('No options');
	const [selectedResident, setSelectedResident] = useState('');
	const [selectedResidentName, setSelectedResidentName] = useState('');
	const [batchMode, setBatchMode] = useState(false);
	const [editingSection, setEditingSection] = useState(null);
	const [loadingResidents, setLoadingResidents] = useState(false);

	useEffect(() => {
		if (
			!state?.isUpdatingPageState &&
			initialCarePlan &&
			initialCarePlan.length > 0
		) {
			setGeneratedPlan(initialCarePlan);
			setId(initialPlanElderId);
			setIsPlanGenerated(true);
		}
	}, []);

	useEffect(() => {
		fetchResidentIds();
		fetchPlanTemplates();
	}, []);

	function onChangeId(value) {
		if (value.length > 10) {
			setIdError({
				status: true,
				msg: 'Only 10 characters allowed for Id',
			});
			return;
		} else {
			setIdError({ status: false, msg: '' });
		}

		const validId = value.replace(/[^a-z0-9-]/g, '');

		if (value !== validId) {
			setIdError({
				status: true,
				msg: 'Only lowercase letters, numbers, and dashes are allowed.',
			});
		} else {
			setIdError({ status: false, msg: '' });
		}

		setId(value);
	}

	function validateInputs() {
		let isValid = true;

		if (!id || id === '') {
			setIdError({ status: true, msg: 'Select resident id' });
			isValid = false;
		}

		if (txt.length === 0) {
			setTxtError({ status: true, msg: 'Upload the pdf file' });
			isValid = false;
		}

		return isValid;
	}

	/**
	 * Step 1: Fetch Resident Profile
	 */
	async function onFetchResidentProfile() {
		setIsLoadingQuestions(true);
		setLoadingMsg('Fetching resident profile');
		const selectedElder = residentFetchedIds.find(
			item =>
				item.elderId === selectedResident.split(' ')[0] ||
				item.name === selectedResident,
		);

		setSelectedResidentName(selectedElder.name);

		let reData;
		if (selectedElder.isFromExternalApi) {
			reData = await fetchExternalElder(selectedElder.id, token);
		} else {
			reData = await fetchElder(selectedElder.id, token);
		}

		if (!reData.error) {
			const { id, elderHomeId, createdDate, adminId, fileKey, ...rest } =
				reData;

			const filteredProfiles = Object.fromEntries(
				Object.entries(rest).filter(([_, value]) => {
					return (
						value != null &&
						value !== '' &&
						value !== 0 &&
						!(Array.isArray(value) && value.length === 0)
					);
				}),
			);

			const flattenedProfiles = flattenObject(filteredProfiles);

			setResident(flattenedProfiles);
			setEditedResident(reData);
			setResidentOriginalData(flattenedProfiles);
			setEditedResidentOriginalData(reData);
			setOnShowFileUpload(true);
			setIsLoadingQuestions(false);
			setLoadingMsg('');
		}
	}

	/**
	 * Step 2: Upload Plan (Generating care categories)
	 */
	async function onUploadPlan() {
		setIsLoading(true);
		setLoadingMsg('Generating care goals');
		const formData = new FormData();

		const selectedElder = residentFetchedIds.find(
			item =>
				item.elderId === selectedResident.split(' ')[0] ||
				item.name === selectedResident,
		);

		const templateJson =
			planTemplates.find(item => item.id === selectedTemplate)
				?.template ?? {};

		fileData.forEach(file => {
			formData.append(
				'uploaded_files',
				new Blob([file], { type: 'text/plain' }),
				file.name,
			);
		});
		if (selectedElder.isFromExternalApi) {
			formData.append('use_api', true);
			formData.append('elder_id', selectedElder.elderId);
		} else {
			formData.append('elder_id', selectedElder.id);
		}
		formData.append('resident_name', selectedElder.name);
		formData.append('input_text', instruction);
		formData.append('user_id', getUserId());
		formData.append('is_demo', isDemoUser);
		formData.append('base_url', central_db_host);
		formData.append('auth_token', token);
		formData.append('client_id', getElderHomeId());
		formData.append('template_config', templateJson);

		const responseData = await uploadInitialCarePlan(formData);

		if (!responseData.error) {
			setSelectionData(responseData.health_issues);
			setMedicalConditionData(responseData.health_issues);
			setSessionId(responseData.session_id);
			setPopupTitle(
				"Medical Conditions Identified from Resident's uploaded File (Unselect any conditions that you do not wish to include in the plan)",
			);
			setPopupBtnState('Next');
			setOnShowFileUpload(false);
			setIsPlanOpen(true);
			setSelectedData(Object.keys(responseData.health_issues));
		} else {
			if (responseData.data && responseData.data.limit_reached) {
				setOpenLimitationModal(true);
				setRemainingGeneration({
					count: 0,
					progress: 100 - (0 / 5) * 100,
					text: `You've generated 5 care plans and have 0 remaining.`,
				});
			} else {
				toast.error(responseData.message);
			}
		}
		setIsLoading(false);
		setIsMultiFileModalOpen(false);
		setLoadingMsg('');
	}

	/**
	 * Step 3: Generate Care Goals
	 */
	async function onGenerateCareGoals() {
		if (selectedData && selectedData.length === 0) {
			setSelectedDataError({
				status: true,
				msg: 'Please select medical conditions',
			});
			return;
		}

		setIsLoadingQuestions(true);
		setLoadingMsg('Generating care plan topics');
		setSelectedMedicalCondition(selectedData);

		const templateJson =
			planTemplates.find(item => item.id === selectedTemplate)
				?.template ?? {};

		const body = {
			user_id: getUserId(),
			session_id: sessionId,
			selected_issues: selectedData,
			is_demo: isDemoUser,
			client_id: getElderHomeId(),
			template_config: templateJson,
		};

		const data = await generateInitialCareGoals(body);
		if (!data.error) {
			setSelectionData(data.care_goals);
			setPopupTitle(
				'The care categories to be included in the Initial care plan (Unselect any categories that are not required in the plan)',
			);
			setPopupBtnState('Generate Care Plan');
			setOnShowFileUpload(false);
			setIsPlanOpen(true);
			setIsMedicalConditionStepComplete(true);
			setSelectedData(Object.keys(data.care_goals));
		} else {
			toast.error(data.message);
		}

		setIsLoadingQuestions(false);
		setLoadingMsg('');
	}

	function onFormatPlan(plan) {
		if (plan) {
			const formattedPlan = Object.keys(plan).map(item => {
				return {
					title: item,
					bodyContent: Object.keys(plan[item]).map(value => {
						return {
							label: sanitizeTitle(value),
							content: checkValueTypes(plan[item][value]),
						};
					}),
				};
			});

			return formattedPlan;
		} else {
			return [];
		}
	}

	/**
	 * Step 04 : Generate Initial Care Plan
	 */
	async function onGenerateInitialCarePlan() {
		if (selectedData && selectedData.length === 0) {
			setSelectedDataError({
				status: true,
				msg: 'Please select sub plans',
			});
			return;
		}

		setIsLoadingQuestions(true);
		setLoadingMsg('Generating care plan');

		const templateJson =
			planTemplates.find(item => item.id === selectedTemplate)
				?.template ?? {};

		const body = {
			user_id: getUserId(),
			client_id: getElderHomeId(),
			session_id: sessionId,
			care_goals: selectedData,
			is_demo: isDemoUser,
			template_config: templateJson,
		};

		const updateInitialPlan = data => {
			if (data.status && data.status.startsWith('Processing')) {
				setLoadingMsg(data.status);
			}

			const formattedPlan =
				data.generated_section &&
				Object.keys(data.generated_section).map(item => {
					return {
						title: item,
						bodyContent: Object.keys(
							data.generated_section[item],
						).map(key => ({
							label: sanitizeTitle(key),
							content: checkValueTypes(
								data.generated_section[item][key],
							),
						})),
					};
				});
			if (formattedPlan) {
				setGeneratedPlan(formattedPlan);
				storeInitialCarePlan(formattedPlan);
			}

			if (data.done) {
				setIsGenerationComplete(true);
				setLoadingMsg('Initial care plan is created');
				const formattedPlanForDB = onFormatPlan(data.care_plan);
				setTimeout(() => {
					setIsLoadingQuestions(false);
					setLoadingMsg('');
					setIsGenerationComplete(false);
				}, 2000);
			}
		};

		await generateInitialCarePlan(body, updateInitialPlan);

		// storeInitialPlanElderId(id);
		onCloseModal();
		onCloseFileUploadModal();
		setIsPlanOpen(false);
		setIsPlanGenerated(true);
	}

	async function storeInitialPlanData() {
		const selectedElder = residentFetchedIds.find(
			item => item.elderId === id.split(' ')[0] || item.name === id,
		);

		const planData = {
			elderHomeId: homeId,
			elderId: selectedElder.id,
			generatedUserId: getUserId(),
			generatedUserName: getUserName(),
			plan: generatedPlan,
		};

		const formData = new FormData();
		formData.append(
			'plan',
			new Blob([JSON.stringify(planData)], {
				type: 'application/json',
			}),
		);

		fileData.forEach(file => {
			formData.append('file', file);
		});

		const response = await addInitialCarePlan(formData, token);

		if (response.error) {
			toast.error(response.message);
		} else {
			toast.success('Plan stored successfully');
		}
	}

	function checkValueTypes(item) {
		if (
			!item ||
			(typeof item === 'string' && !item.trim()) ||
			(Array.isArray(item) && !item.length) ||
			(item &&
				typeof item === 'object' &&
				!Array.isArray(item) &&
				!Object.keys(item).length)
		) {
			return ['No data'];
		}

		if (
			Array.isArray(item) &&
			item.every(value => typeof value === 'object')
		) {
			const mergedArray = item.flatMap(obj =>
				Object.entries(obj).map(([key, value]) => `${key}:${value}`),
			);

			return mergedArray;
		} else if (typeof item === 'string') {
			return [item];
		} else {
			return item;
		}
	}

	function readTextFile(e) {
		const ext = e[0].path.split('.').pop().toLowerCase();

		if (!['txt', 'pdf', 'docx', 'xlsx'].includes(ext)) {
			setTxtError({ status: true, msg: 'Invalid file format.' });
			return;
		}

		if (e[0] && e[0].size === 0) {
			setTxtError({
				status: true,
				msg: 'The selected file is empty.',
			});
			return;
		}

		if (e[0] && e[0].size > 5242880) {
			setTxtError({
				status: true,
				msg: 'Maximum upload file size is 5MB.',
			});
			return;
		}

		setTxt([...txt, e[0]]);
		setTxtError({ status: false, msg: '' });
		setFileData([...fileData, e[0]]);
	}

	const handleAccordionExpansion = id => (_, isExpanded) => {
		setExpandedAccordion(isExpanded ? id : false);
	};

	function onClickPopupBtn() {
		if (popupBtnState === 'Next') {
			onGenerateCareGoals();
		} else {
			onGenerateInitialCarePlan();
		}
	}

	function onCloseModal() {
		setIsPlanOpen(false);
		setSelectionData({});
		setPopupTitle('');
		setPopupBtnState('');
	}

	function onCloseFileUploadModal() {
		setIsMultiFileModalOpen(false);
		setTxt([]);
		setTxtError({ status: false, msg: '' });
		setFileData([]);
		setInstruction('');
		setSelectedTemplate('');
	}

	function onSelectItem(item) {
		setSelectedDataError(INITIAL_ERROR_STATE);
		if (selectedData.includes(item)) {
			const clone = selectedData.filter(value => value !== item);
			setSelectedData(clone);
		} else {
			setSelectedData([...selectedData, item]);
		}
	}

	function convertBulletStringToArray(str) {
		return str.split('\n').map(line => line.replace(/^•\s*/, ''));
	}

	function handlePlanChange(value, sectionIndex, itemIndex, contentIndex) {
		const updated = [...generatedPlan];

		updated[sectionIndex].bodyContent[itemIndex].content[contentIndex] =
			value;

		setGeneratedPlan(updated);
	}

	function onCreateNewPlan() {
		storeInitialCarePlan([]);
		setFileData([]);
		setTxt([]);
		setId(null);
		setIsPlanGenerated(false);
		setOnShowFileUpload(false);
		setGeneratedPlan({});
		setSelectedTemplate('');
		setSelectedResident('');
	}

	async function onGeneratePdf() {
		if (isLoadingQuestions) {
			return;
		}

		const fileName = `SenzCare.AI_InitialCarePlan_${id}.pdf`;
		const elderId = onShowFileUpload ? id.split(' ')[0] : 'N/A';
		const name = onShowFileUpload ? id.split('(')[1].split(')')[0] : id;
		const blob = await pdf(
			<PlanDocument
				elderId={elderId}
				elderName={name}
				documentData={generatedPlan}
				title={'Initial Care Plan'}
			/>,
		).toBlob();
		saveAs(blob, fileName);
	}

	function onGoBackToMedicalConditionStep() {
		setSelectionData(medicalConditionData);
		setSelectedData(selectedMedicalCondition);
		setIsMedicalConditionStepComplete(false);
		setPopupTitle(
			"Medical Conditions Identified from Resident's uploaded File (Unselect any conditions that you do not wish to include in the plan)",
		);
		setPopupBtnState('Next');
	}

	async function fetchResidentIds() {
		setLoadingResidents(true);
		const allResResponse = await getResidentIds(homeId, token);
		const response = await getResidentIds(homeId, token, 'initial');

		if (allResResponse.message === 'Success') {
			setShowNoResidentBanner(
				allResResponse.content.length > 0 ? false : true,
			);
			setNoResidentMsg(
				allResResponse.content.length > 0
					? 'All elders have care plans'
					: 'No options',
			);
		}

		if (response.message === 'Success') {
			const newIds = response.content.map(item =>
				item.isFromExternalApi
					? item.name
					: `${item.elderId} (${item.name})`,
			);

			const structuredResidents = response.content.map((item, index) => {
				const isExternal = item.isFromExternalApi;
				const newId = isExternal
					? item.name
					: `${item.elderId} (${item.name})`;

				// Get initials from name
				const initials = item.name
					.split(' ')
					.map(word => word[0])
					.join('')
					.toUpperCase()
					.slice(0, 2);

				// Random color
				const avatarColor = colors[index % colors.length];

				// const description = `Room 12 · Dementia · ${isExternal ? 'Api' : 'Manual'} onboarded — no plan`;
				const description = `${isExternal ? 'Api' : 'Manual'} onboarded — no plan`;

				return {
					id: newId,
					initials,
					name: item.name,
					subtitle: description,
					avatar: avatarColor,
				};
			});

			setResidentFetchedIds(response.content);
			setResidentIds(structuredResidents);
			setLoadingResidents(false);
		} else {
			toast.error(response.message);
		}
	}

	async function fetchPlanTemplates() {
		const response = await getTemplateByType(homeId, 'Care Plan');

		if (!response.error) {
			const isTempAvailable = response.content.length > 0 ? true : false;
			if (isTempAvailable) {
				const defaultTemplate = {
					templateName: 'Default Template',
					id: null,
				};
				setPlanTemplates([defaultTemplate, ...response.content]);
			}
			setIsUserHasTemplates(isTempAvailable);
		}
	}

	function onAddNewId(event) {
		setId(event.target.value);
		setIdError({ status: false, msg: '' });
	}

	function onRemoveFiles(content) {
		const clone = fileData.filter(value => value.name !== content.name);
		setTxt(clone);
		setFileData(clone);
	}

	function onOpenFileUploadModal() {
		if (!id || id === '') {
			setIdError({ status: true, msg: 'Select resident id' });
			return;
		}
		setIsMultiFileModalOpen(true);
	}

	function onResetLoaderAndCancelRequest() {
		cancelApiRequest();
		setIsLoading(false);
		setIsLoadingQuestions(false);
		setLoadingMsg('');
	}

	function flattenObject(obj, parentKey = '') {
		const flattened = {};

		for (const [key, value] of Object.entries(obj)) {
			const newKey = parentKey ? `${parentKey}.${key}` : key;

			if (value && typeof value === 'object' && !Array.isArray(value)) {
				Object.assign(flattened, flattenObject(value, newKey));
			} else if (Array.isArray(value)) {
				flattened[newKey] = value.join(', ');
			} else {
				flattened[newKey] = value;
			}
		}

		return flattened;
	}

	function onChangeSelection(e, value) {
		setId(value);
		setIdError({ status: false, msg: '' });
		setFileData([]);
		setInstruction('');
		onFetchResidentProfile(value);
	}

	function updateNested(obj, path, value) {
		const newPath = [...path];
		const keyName = newPath.pop();

		let newObj = { ...obj }; // full object (here it will be resident profile)
		let current = newObj;

		for (let i = 0; i < newPath.length; i++) {
			const pathKey = newPath[i];
			const nextKey = newPath[i + 1];

			//here checking the pathKey data is array, object or string
			if (Array.isArray(current[pathKey])) {
				current[pathKey] = [...current[pathKey]];
				current = current[pathKey];
			} else if (
				typeof current[pathKey] === 'object' &&
				current[pathKey] !== null
			) {
				current[pathKey] = { ...current[pathKey] };
				current = current[pathKey];
			} else {
				current[pathKey] = nextKey && /^\d+$/.test(nextKey) ? [] : {};
				current = current[pathKey];
			}
		}

		current[keyName] =
			typeof value === 'string' && keyName !== 'dateOfBirth'
				? [value]
				: value;

		return newObj;
	}

	function onChangeProfile(e, key) {
		setIsProfileEdited(true);
		const inputValue = e.target.value;
		setResident(prevState => ({
			...prevState,
			[key]: inputValue,
		}));

		setEditedResident(prevState => {
			let newValue = inputValue;

			// Force dateOfBirth to YYYY-MM-DD format
			if (key === 'dateOfBirth' || key.includes('dateOfBirth')) {
				newValue = formatDate(inputValue);
			}
			// Handle comma-separated values for other fields
			else if (inputValue.includes(',')) {
				newValue = inputValue
					.split(',')
					.map(item => item.trim())
					.filter(Boolean);
			}

			const path = key.split(/\.|\[/); // Splitting the key if it has . it will split else one key

			return updateNested(prevState, path, newValue);
		});
	}

	function formatDate(dateInput) {
		// Handle different input formats
		if (!dateInput) return '';

		// If already valid ISO format, return as-is
		const date = new Date(dateInput);
		if (!isNaN(date.getTime())) {
			return date.toISOString().split('T')[0]; // YYYY-MM-DD
		}

		// Try parsing common formats: DD/MM/YYYY, MM/DD/YYYY, DD-MM-YYYY
		const parts = dateInput.match(/(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})/);
		if (parts) {
			const day = parseInt(parts[1]);
			const month = parseInt(parts[2]) - 1; // JS months are 0-indexed
			const year = parseInt(parts[3]);

			const date = new Date(year, month, day);
			if (!isNaN(date.getTime())) {
				return date.toISOString().split('T')[0];
			}
		}

		return dateInput; // Fallback
	}

	async function onSaveChange() {
		const id = editedResident.elderId;
		const selectedElder = residentFetchedIds.find(
			item => item.elderId === id,
		);

		const route = `${selectedElder.isFromExternalApi ? 'external/elder' : 'elder'}/${selectedElder.id}`;

		const response = await storeUpdatedElder(token, editedResident, route);
		if (response.message === 'Success') {
			toast.success('Resident profile updated');

			const { id, elderHomeId, createdDate, adminId, fileKey, ...rest } =
				response.content;

			const filteredProfiles = Object.fromEntries(
				Object.entries(rest).filter(([_, value]) => {
					return (
						value != null &&
						value !== '' &&
						value !== 0 &&
						!(Array.isArray(value) && value.length === 0)
					);
				}),
			);
			const flattenedProfiles = flattenObject(filteredProfiles);
			setResidentOriginalData(flattenedProfiles);
			setIsProfileEdited(false);
			setEditing(false);
		} else {
			toast.error(response.message);
		}
	}

	function onClearChange() {
		setResident(residentOriginalData);
		setEditedResident(editedResidentOriginalData);
		setIsProfileEdited(false);
		setEditing(false);
	}

	function onRenderPlan() {
		if (onShowFileUpload && !isPlanOpen) {
			return (
				<>
					{/* Stepper */}
					<Card
						elevation={0}
						sx={{
							borderRadius: 3,
							border: '1px solid #d9e5e7',
							mb: 3,
						}}>
						<CardContent>
							<Stepper activeStep={2} alternativeLabel>
								{steps.map(step => (
									<Step key={step}>
										<StepLabel
											StepIconProps={{
												sx: {
													'&.Mui-active': {
														color: teal,
													},
													'&.Mui-completed': {
														color: teal,
													},
												},
											}}>
											{step}
										</StepLabel>
									</Step>
								))}
							</Stepper>
						</CardContent>
					</Card>
					<>
						{/* Profile */}
						<Card
							elevation={0}
							sx={{
								borderRadius: 3,
								border: '1px solid #d9e5e7',
								mb: 3,
							}}>
							<CardContent sx={{ p: 3 }}>
								<Stack
									direction="row"
									alignItems="center"
									justifyContent="space-between"
									sx={{ mb: 2 }}>
									<Stack
										direction="row"
										spacing={1}
										alignItems="center">
										<PersonIcon sx={{ color: '#00b7a8' }} />

										<Typography
											fontWeight={600}
											fontSize={13}>
											{`${selectedResidentName} - Profile Summary`}
										</Typography>
									</Stack>
								</Stack>

								<Box
									sx={{
										display: 'flex',
										justifyContent: 'flex-end',
										gap: 1,
										mb: 2,
									}}>
									<Button
										size="small"
										variant="outlined"
										startIcon={
											<Edit
												fontSize="small"
												sx={{ width: 12, height: 12 }}
											/>
										}
										onClick={() => {
											editing
												? onSaveChange()
												: setEditing(!editing);
										}}
										sx={{
											minWidth: 0,
											height: 26,
											fontSize: 11,
											textTransform: 'none',
											borderRadius: '6px',
											borderColor: border,
											color: '#1a2424',
											background: '#fff',
										}}>
										{editing ? 'Done' : 'Edit'}
									</Button>

									<Button
										size="small"
										variant="outlined"
										onClick={onClearChange}
										sx={{
											minWidth: 0,
											height: 26,
											fontSize: 11,
											textTransform: 'none',
											borderRadius: '6px',
											borderColor: '#ffd6d6',
											color: '#c62828',
											background: '#fff',
										}}>
										Reset
									</Button>
								</Box>

								<Grid container spacing={2}>
									{Object.entries(resident).map(
										([label, value]) => (
											<Grid
												item
												xs={12}
												md={6}
												key={label}>
												<Box
													sx={{
														pb: 1.5,
														borderBottom:
															'1px solid #edf2f3',
													}}>
													<Typography
														sx={{
															color: '#6b8888',
															fontWeight: 700,
															fontSize: 11,
														}}>
														{label
															.split('.')
															.pop()
															.replace(
																/^./,
																char =>
																	char.toUpperCase(),
															)}
													</Typography>

													{editing ? (
														<TextField
															fullWidth
															multiline
															variant="outlined"
															value={value}
															disabled={
																label ===
																'elderId'
															}
															onChange={e =>
																onChangeProfile(
																	e.target
																		.value,
																	label,
																)
															}
															placeholder="Enter text here"
															sx={{
																mt: 1,

																'& .MuiOutlinedInput-root':
																	{
																		height: 35,
																		fontSize:
																			'13px',
																		borderRadius:
																			'8px',
																		background:
																			'#fff',
																	},
															}}
														/>
													) : (
														<Typography
															sx={{
																mt: 0.5,
																fontSize: 13,
																fontWeight: 500,
																color:
																	label ===
																	'Risk Level'
																		? '#c17b00'
																		: '#102c2c',
															}}>
															{value || 'N/A'}
														</Typography>
													)}
												</Box>
											</Grid>
										),
									)}
								</Grid>
							</CardContent>
						</Card>

						{/* Context */}
						<Card
							elevation={0}
							sx={{
								borderRadius: 3,
								border: '1px solid #d9e5e7',
							}}>
							<CardContent sx={{ p: 3 }}>
								<Stack
									direction="row"
									spacing={1}
									alignItems="center"
									sx={{ mb: 2 }}>
									<AddCommentIcon sx={{ color: '#00b7a8' }} />

									<Typography fontWeight={600} fontSize={13}>
										Add context for this care plan
									</Typography>
								</Stack>

								<Stack
									direction="row"
									spacing={1}
									sx={{ mb: 2 }}>
									<TextField
										fullWidth
										value={instruction}
										placeholder="Enter context..."
										onChange={e =>
											setInstruction(e.target.value)
										}
										sx={{
											'& .MuiInputBase-input': {
												fontSize: '13px',
												height: '10px',
												borderRadius: '5px',
												'&::placeholder': {
													fontSize: '13px',
												},
											},
										}}
									/>
									{/* <IconButton
										sx={{
											width: 40,
											height: 40,
											border: '1px solid #cde7e5',
											backgroundColor: '#eefcf9',
											borderRadius: '50%',
											'&:hover': {
												backgroundColor: '#dff8f3',
											},
										}}>
										<MicIcon />
									</IconButton>
									<Button
										variant="contained"
										sx={{
											textTransform: 'none',
											background: teal,
											color: '#083838',
											fontWeight: 700,
											borderRadius: '5px',
											boxShadow: 'none',
											'&:hover': {
												background: teal,
												boxShadow: 'none',
											},
										}}>
										Add
									</Button> */}
								</Stack>

								<Dropzone
									onDrop={acceptedFiles =>
										readTextFile(acceptedFiles)
									}>
									{({
										getRootProps,
										getInputProps,
										isDragActive,
									}) => (
										<Paper
											{...getRootProps()}
											elevation={0}
											sx={{
												borderRadius: 3,
												border: '2px dashed #a5e8e1',
												background: '#eefcf9',
												py: 4,
												textAlign: 'center',
												cursor: 'pointer',
											}}>
											<input {...getInputProps()} />
											<UploadFileIcon
												sx={{
													fontSize: 40,
													color: '#00b7a8',
													mb: 1,
												}}
											/>
											<>
												<Typography
													fontWeight={700}
													fontSize={14}
													color={dark}>
													Upload additional files
												</Typography>

												<Typography
													variant="body2"
													sx={{
														color: '#6b8888',
														mt: 0.5,
														fontSize: 12,
													}}>
													Specialist letters, previous
													assessments, handover notes
												</Typography>
											</>
										</Paper>
									)}
								</Dropzone>
								{fileData && fileData.length > 0 && (
									<Grid
										container
										flexDirection={'column'}
										marginTop={'15px'}>
										<Grid
											container
											alignItems="center"
											spacing={2}>
											{fileData.map((file, index) => {
												return (
													<Grid
														item
														xs={6}
														sm={4}
														md={3}
														key={index}>
														<Grid
															container
															alignItems="center"
															justifyContent="space-between"
															wrap="nowrap"
															sx={{
																border: '1px solid #D9D9D9',
																borderRadius:
																	'8px',
																padding:
																	'6px 10px',
																maxWidth:
																	'220px',
																backgroundColor:
																	'#FAFAFA',
															}}>
															<Grid
																item
																xs
																sx={{
																	overflow:
																		'hidden',
																}}>
																<Typography
																	fontSize="13px"
																	noWrap
																	title={
																		file?.name
																	}>
																	{file?.name}
																</Typography>
															</Grid>
															<Grid item>
																<IconButton
																	disableRipple
																	style={{
																		padding: 0,
																		marginLeft:
																			'6px',
																	}}
																	disabled={
																		isLoading
																	}
																	onClick={() =>
																		onRemoveFiles(
																			file,
																		)
																	}>
																	<CircleClose />
																</IconButton>
															</Grid>
														</Grid>
													</Grid>
												);
											})}
										</Grid>
									</Grid>
								)}
								<Button
									disabled={isLoading || isLoadingQuestions}
									variant="contained"
									onClick={() =>
										!isUserHasTemplates
											? onUploadPlan()
											: setShowTemplates(true)
									}
									sx={{
										textTransform: 'none',
										borderRadius: 2,
										background: teal,
										color: '#083838',
										fontWeight: 500,
										boxShadow: 'none',
										'&:hover': {
											background: '#00b2a3',
											boxShadow: 'none',
										},
										fontSize: 13,
										marginTop: '5px',
									}}>
									Next
								</Button>
							</CardContent>
						</Card>
					</>
				</>
			);
		} else if (isPlanOpen && !onShowFileUpload) {
			const cState = popupBtnState === 'Next';
			const data = cState ? medicalConditionData : selectionData;
			const title = cState ? 'Care Goals' : 'Care Plan Topics';

			return (
				<Grid container width={'100%'}>
					<Card
						elevation={0}
						sx={{
							borderRadius: 3,
							border: '1px solid #d9e5e7',
							width: '100%',
						}}>
						<CardContent sx={{ p: 3 }}>
							<Stack
								direction="row"
								spacing={1}
								alignItems="center"
								sx={{ mb: 1 }}>
								<ListAltIcon sx={{ color: '#00b7a8' }} />

								<Typography fontWeight={600} fontSize={13}>
									{title}
								</Typography>
							</Stack>

							<Typography
								sx={{
									color: '#6b8888',
									mb: 2,
									fontSize: 12,
								}}>
								{`Select ${title.toLowerCase()} to include — AI generates
								detailed content for each.`}
							</Typography>

							<Stack spacing={1.2}>
								{Object.keys(data).map(cat => (
									<Paper
										key={cat.label}
										elevation={0}
										sx={{
											border: cat.checked
												? '1px solid #00c4b4'
												: '1px solid #d9e5e7',
											background: cat.checked
												? '#eefcf9'
												: '#ffffff',
											borderRadius: 2,
											p: 1,
										}}>
										<Stack
											direction="row"
											spacing={1.2}
											alignItems="center">
											<Checkbox
												checked={selectedData.includes(
													cat,
												)}
												onClick={() =>
													onSelectItem(cat)
												}
												sx={{
													color: teal,
													'&.Mui-checked': {
														color: teal,
													},
												}}
											/>

											<Typography
												fontWeight={600}
												fontSize={13}
												color={
													selectedData.includes(cat)
														? '#102c2c'
														: '#7b8b8b'
												}>
												{cat}
											</Typography>
										</Stack>
									</Paper>
								))}
							</Stack>
							<Button
								disabled={isLoading || isLoadingQuestions}
								onClick={onClickPopupBtn}
								variant="contained"
								startIcon={!cState ? <AutoAwesomeIcon /> : null}
								sx={{
									mt: 2,
									py: 1.2,
									borderRadius: 2,
									textTransform: 'none',
									background: teal,
									color: '#083838',
									fontWeight: 600,
									fontSize: 13,
									boxShadow: 'none',
									'&:hover': {
										background: '#00b2a3',
										boxShadow: 'none',
									},
								}}>
								{popupBtnState}
							</Button>
						</CardContent>
					</Card>
				</Grid>
			);
		} else {
			return <></>;
		}
	}

	return (
		<MainGrid>
			{!isPlanGenerated ? (
				<Box sx={{ width: '100%' }}>
					{/* Header */}
					<Box sx={{ mb: 3 }}>
						<Typography
							sx={{
								fontSize: '22px',
								fontWeight: 600,
								color: dark,
								mb: 0.5,
							}}>
							Initial Care Plan Generation
						</Typography>

						<Typography variant="body2" sx={{ color: '#6b7a7a' }}>
							Residents without a care plan — generate
							individually or in batch
						</Typography>
					</Box>

					{/* Resident selector */}
					<Card
						elevation={0}
						sx={{
							borderRadius: 3,
							border: '1px solid #d9e5e7',
							mb: 3,
						}}>
						<CardContent sx={{ p: 3 }}>
							<Stack
								direction="row"
								alignItems="center"
								justifyContent={'space-between'}
								spacing={1}
								sx={{ mb: 1.5 }}>
								<Grid
									container
									flexDirection={'row'}
									alignItems={'center'}>
									<GroupsIcon sx={{ color: '#00b7a8' }} />
									<Typography
										fontWeight={600}
										color="#102c2c"
										marginLeft={'5px'}
										fontSize={13}>
										Residents Without Care Plans
									</Typography>
								</Grid>

								<Chip
									label={`${residentIds.length} Residents`}
									size="small"
									sx={{
										ml: 'auto',
										background: '#fff4df',
										color: '#9a6400',
										fontWeight: 500,
										fontSize: 12,
									}}
								/>
							</Stack>

							<Typography
								sx={{ color: '#6b7a7a', mb: 2, fontSize: 12 }}>
								Select a resident to generate their initial care
								plan, or run batch generation for all.
							</Typography>

							<Stack spacing={1.2} height={300} overflow="auto">
								{residentIds.length > 0 ? (
									residentIds.map(resident => (
										<Paper
											key={resident.id}
											elevation={0}
											onClick={() => {
												setSelectedResident(
													resident.id,
												);
												setId(resident.id);
											}}
											sx={{
												border:
													selectedResident ===
													resident.id
														? '1px solid #00c4b4'
														: '1px solid #d9e5e7',
												background:
													selectedResident ===
													resident.id
														? '#eefcf9'
														: '#ffffff',
												borderRadius: 2,
												p: 1.5,
												cursor: 'pointer',
											}}>
											<Stack
												direction="row"
												alignItems="center"
												justifyContent="space-between">
												<Stack
													direction="row"
													alignItems="center">
													<Radio
														checked={
															selectedResident ===
															resident.id
														}
													/>
													<Avatar
														sx={{
															width: 34,
															height: 34,
															fontSize: 13,
															background:
																resident.avatar,
														}}>
														{resident.initials}
													</Avatar>

													<Box
														sx={{
															marginLeft: '10px',
														}}>
														<Typography
															fontWeight={600}
															fontSize={12}>
															{resident.name}
														</Typography>

														<Typography
															sx={{
																color: '#6b7a7a',
																fontSize: 11,
															}}>
															{resident.subtitle}
														</Typography>
													</Box>
												</Stack>

												<Chip
													label="No plan"
													size="small"
													sx={{
														background: '#fff4df',
														color: '#9a6400',
														fontWeight: 500,
														fontSize: 12,
													}}
												/>
											</Stack>
										</Paper>
									))
								) : (
									<Grid
										container
										mt={2}
										alignItems="center"
										justifyContent={'center'}>
										{loadingResidents &&
										residentIds.length === 0 ? (
											<Grid
												container
												flexDirection={'column'}>
												<Skeleton
													variant="rectangular"
													height={60}
													sx={{
														borderRadius: '10px',
													}}
												/>
												<Skeleton
													variant="rectangular"
													height={60}
													sx={{
														marginTop: '10px',
														borderRadius: '10px',
													}}
												/>
											</Grid>
										) : (
											<Card
												sx={{
													borderRadius: '18px',
													border: '1px solid #d8e8e8',
													boxShadow: 'none',
													alignItems: 'center',
													width: '100%',
												}}>
												<CardContent
													sx={{
														p: 3,
														display: 'flex',
														alignItems: 'center',
														justifyContent:
															'center',
														minHeight: 50,
														color: '#6b8888',
														fontSize: 13,
														fontWeight: 500,
													}}>
													{noResidentMsg}
												</CardContent>
											</Card>
										)}
									</Grid>
								)}
							</Stack>
							<Stack
								direction="row"
								spacing={1.5}
								sx={{ mt: 2.5 }}>
								<Button
									variant="contained"
									startIcon={<AssignmentIcon />}
									onClick={onFetchResidentProfile}
									sx={{
										textTransform: 'none',
										borderRadius: 2,
										background: teal,
										color: '#083838',
										fontWeight: 500,
										boxShadow: 'none',
										'&:hover': {
											background: '#00b2a3',
											boxShadow: 'none',
										},
										fontSize: 13,
									}}>
									Generate for Selected Resident
								</Button>

								{/* <Button
								variant="outlined"
								startIcon={<AutoAwesomeIcon />}
								onClick={() => setBatchMode(true)}
								sx={{
									textTransform: 'none',
									borderRadius: 2,
									color: '#1a2424',
									border: '1px solid #d8e8e8',
									fontSize: 13,
									fontWeight: 500,
								}}>
								Batch Generate for All 3
							</Button> */}
							</Stack>
						</CardContent>
					</Card>

					{/* Selected Resident */}
					{(onShowFileUpload || isPlanOpen) && (
						<Paper
							elevation={0}
							sx={{
								borderRadius: 2,
								border: '1px solid #d9e5e7',
								p: 1.5,
								mb: 2,
								background: '#ffffff',
							}}>
							<Stack
								direction="row"
								spacing={1}
								alignItems="center">
								<PersonIcon sx={{ color: '#00b7a8' }} />

								<Typography
									fontWeight={600}
									fontSize={13}
									color="#135050">
									Generating for:
								</Typography>

								<Typography fontWeight={700} fontSize={13}>
									{`${selectedResidentName} · Room 12 · Dementia`}
								</Typography>
							</Stack>
						</Paper>
					)}
					{onRenderPlan()}
				</Box>
			) : (
				<GeneratedInitialPlan
					plan={generatedPlan}
					isLoading={isLoadingQuestions}
					onGoBack={onCreateNewPlan}
					elder={selectedResidentName}
					onPlanChange={handlePlanChange}
					setEditingSection={setEditingSection}
					editingSection={editingSection}
					onStorePlan={storeInitialPlanData}
					onDownload={onGeneratePdf}
				/>
			)}
			<PlanTemplatesPopup
				isModalOpen={showTemplates}
				templates={planTemplates}
				selected={selectedTemplate}
				onSelect={value => setSelectedTemplate(value)}
				onSubmit={() => {
					onUploadPlan();
					setShowTemplates(false);
				}}
				onClose={() => {
					setShowTemplates(false);
					setSelectedTemplate('');
				}}
			/>
			<ToastBanner
				open={isLoadingQuestions || isLoading}
				message={loadingMsg}
				icon={<PlanIcon color={'#FFFFFF'} />}
				isCompleted={isGenerationComplete}
			/>
		</MainGrid>
	);
}
